import type { IGlobalTab } from '@carboncopyco/types/desktop';
import { useEffect, useState } from 'react';
import { UserEventsEnum } from '../../shared/enums/UserEventsEnum';
import {
  supportsMouseAnimation,
  supportsTextExtraction,
} from '../../shared/globalTabUtil';
import { useTabs } from '../store/context';
import useElectronListener from './useElectronListener';

type NavigationChangeMessage = {
  canGoBack: boolean;
  canGoForward: boolean;
  isLoading: boolean;
  innerTabId: number;
};

export const useNavigation = (tab: IGlobalTab) => {
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { activeGlobalTabId } = useTabs();
  const isActiveTab = activeGlobalTabId === tab.id;

  const goBack = () => {
    if (!tab.activeInnerTabId) return;
    window.dsDesktop.navigation.goBack(tab?.activeInnerTabId);
  };
  const goForward = () => {
    if (!tab.activeInnerTabId) return;
    window.dsDesktop.navigation.goForward(tab?.activeInnerTabId);
  };

  const isInLocation = (urlInclude: string) => {
    const url = window?.location?.href;
    return url?.includes(urlInclude);
  };

  const onNavigationChanged = (message: NavigationChangeMessage) => {
    if (tab.activeInnerTabId && tab.activeInnerTabId === message.innerTabId) {
      setCanGoBack(message.canGoBack);
      setCanGoForward(message.canGoForward);
      setIsLoading(message.isLoading);
    }
  };

  useEffect(() => {
    if (tab?.activeInnerTabId && isActiveTab) {
      setCanGoBack(false);
      setCanGoForward(false);
      setIsLoading(false);
      window.dsDesktop.navigation.requestBrowserNavigationHistory(
        tab.activeInnerTabId
      );
    }
  }, [tab?.activeInnerTabId, isActiveTab]);

  useElectronListener<NavigationChangeMessage>(
    UserEventsEnum.ON_BROWSER_NAVIGATION_CHANGED,
    onNavigationChanged
  );
  useElectronListener(
    UserEventsEnum.VIEW_TO_APP_VIEW_DID_START_LOADING,
    (innerTabId: number) => {
      if (innerTabId === tab.activeInnerTabId) {
        setIsLoading(true);
      }
    }
  );
  useElectronListener(
    UserEventsEnum.VIEW_TO_APP_VIEW_DID_STOP_LOADING,
    (innerTabId: number) => {
      if (innerTabId === tab.activeInnerTabId) {
        setIsLoading(false);
      }
    }
  );
  useElectronListener(
    UserEventsEnum.VIEW_TO_APP_VIEW_DID_FINISH_LOAD,
    (innerTabId: number) => {
      console.debug(
        '[TextExtraction] - UserEventsEnum.VIEW_TO_APP_VIEW_DID_FINISH_LOAD'
      );
      if (innerTabId === tab.activeInnerTabId) {
        if (supportsMouseAnimation(tab))
          window.dsDesktop.demostacking.updateAnimationSettings({});
        setIsLoading(false);

        if (supportsTextExtraction(tab)) {
          window.dsDesktop.textExtraction.updateSelected(tab.id);
        }
      }
    }
  );

  return {
    isInLocation,
    canGoBack,
    canGoForward,
    isLoading,
    goBack,
    goForward,
  };
};
